import React, { useState, useEffect, useMemo } from "react";
import {
  ComposedChart,
  Bar,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  getTransportStatistics,
  getTransportStatisticsByBranch,
  getTransportChanges,
} from "../../../services/partner.service.ts";
import { notification } from "../../../utils/Notifications.tsx";
import ChartSkeleton from "./ChartSkeleton.tsx";
import { getProfileImage } from "../../../services/profileImageCache.ts";
import { Tooltip, Menu, MenuItem } from "@mui/material";
import moment from "moment";
import "moment/locale/es";
moment.locale("es");

interface TransportStatistics {
  total: number;
  byMethod: Array<{
    method: number | null;
    name: string;
    count: number;
    percentage: string;
  }>;
  stats: Array<{
    name: string;
    count: number;
  }>;
}

interface BranchTransportStats {
  branch: {
    id: number;
    name: string;
  };
  total: number;
  byMethod: Array<{
    method: number | null;
    name: string;
    count: number;
    percentage: string;
  }>;
}

interface TransportChange {
  id: number;
  partnerId: number;
  name: string;
  branch: string;
  previousMethod: string;
  newMethod: string;
  changedAt: string;
  weekNumber: number;
  year: number;
}

interface TransportChangeWithImage extends TransportChange {
  profileImage: string | null;
}

const toTitleCase = (text: string): string => {
  if (!text) return "";
  return text
    .toLowerCase()
    .split(/\s+/)
    .map((word) => {
      if (word.length === 0) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};

const formatDateSpanish = (date: string): string => {
  const fechaFormateada = moment(date).format("D [de] MMMM [de] YYYY");
  return fechaFormateada;
};

const TransportStats: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [transportStats, setTransportStats] =
    useState<TransportStatistics | null>(null);
  const [branchTransportStats, setBranchTransportStats] = useState<
    BranchTransportStats[]
  >([]);
  const [transportChanges, setTransportChanges] = useState<
    TransportChangeWithImage[]
  >([]);
  const [days, setDays] = useState(7);
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    partnerId: number;
  } | null>(null);

  const fetchTransportData = async () => {
    try {
      setLoading(true);
      const [statsResponse, branchStatsResponse, changesResponse] =
        await Promise.all([
          getTransportStatistics(),
          getTransportStatisticsByBranch(),
          getTransportChanges(days, undefined, undefined, 50),
        ]);
      console.log("Transport Stats Response:", statsResponse);
      console.log("Branch Transport Stats Response:", branchStatsResponse);
      console.log("Transport Changes Response:", changesResponse);
      setTransportStats(statsResponse);
      setBranchTransportStats(branchStatsResponse);

      const changesWithImages = await Promise.all(
        changesResponse.map(async (change: TransportChange) => {
          const profileImage = await getProfileImage(change.partnerId);
          return {
            ...change,
            profileImage,
          };
        })
      );
      setTransportChanges(changesWithImages);
    } catch (error) {
      console.error("Error fetching transport data:", error);
      notification(
        "Error",
        "Error al obtener las estadísticas de transporte",
        "danger"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransportData();
  }, []);

  useEffect(() => {
    if (days) {
      fetchTransportData();
    }
  }, [days]);

  const chartData = useMemo(() => {
    if (!branchTransportStats || branchTransportStats.length === 0) {
      console.log("No branch transport stats available");
      return [];
    }

    console.log("Processing branch transport stats:", branchTransportStats);

    return branchTransportStats.map((branch) => {
      const data: any = {
        name:
          branch.branch.name.length > 15
            ? branch.branch.name.substring(0, 15) + "..."
            : branch.branch.name,
        fullName: branch.branch.name,
        total: branch.total,
      };

      branch.byMethod.forEach((method) => {
        if (method.method !== null && method.count > 0 && method.name) {
          data[method.name] = method.count;
        }
      });

      return data;
    });
  }, [branchTransportStats]);

  const transportMethods = useMemo(() => {
    if (!transportStats) return [];
    return transportStats.byMethod
      .filter((item) => item.count > 0)
      .map((item) => ({
        name: item.name,
        count: item.count,
        percentage: item.percentage,
      }));
  }, [transportStats]);

  const allMethods = useMemo(() => {
    if (chartData.length === 0) return [];
    return Object.keys(chartData[0]).filter(
      (key) => key !== "name" && key !== "fullName" && key !== "total"
    );
  }, [chartData]);

  const getColorForMethod = (method: string): string => {
    const colorMap: { [key: string]: string } = {
      "Carro propio": "#3b82f6",
      Carro: "#3b82f6",
      Camión: "#8b5cf6",
      Bicicleta: "#10b981",
      Caminando: "#f59e0b",
      Motocicleta: "#ef4444",
      "No especificado": "#9ca3af",
    };

    if (colorMap[method]) {
      return colorMap[method];
    }

    for (const [key, color] of Object.entries(colorMap)) {
      if (method.includes(key) || key.includes(method)) {
        return color;
      }
    }
    return "#6b7280";
  };

  const getGradientId = (method: string): string => {
    return `color${method.replace(/\s+/g, "")}`;
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div style={styles.customTooltip}>
          <p style={styles.tooltipLabel}>{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color, margin: "4px 0" }}>
              {`${entry.name}: ${entry.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <ChartSkeleton
        height={400}
        headerTitle="Medios de Transporte"
        headerIcon="fa-solid fa-car"
      />
    );
  }

  return (
    <div
      style={{ ...styles.chartCard, display: "flex", flexDirection: "column" }}
    >
      <div style={styles.chartHeader}>
        <div style={styles.chartHeaderContent}>
          <h3 style={styles.chartTitle}>
            <i className="fa-solid fa-car" style={styles.chartTitleIcon}></i>
            Medios de Transporte por Sucursal
          </h3>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <select
              className="form-select"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              style={{
                padding: "6px 12px",
                borderRadius: "6px",
                border: "1px solid #e5e7eb",
                fontSize: "13px",
                width: "auto",
              }}
            >
              <option value={7}>Últimos 7 días</option>
              <option value={30}>Últimos 30 días</option>
              <option value={90}>Últimos 90 días</option>
            </select>
            <div style={styles.statsBadge}>
              <span style={styles.statsText}>
                {transportChanges.length} cambios
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Contenedor principal con dos columnas */}
      <div style={styles.mainContent}>
        {/* Columna izquierda: Estadísticas */}
        <div style={styles.statsColumn}>
          {/* Desglose por método */}
          {transportMethods.length > 0 && (
            <div style={styles.breakdownSection}>
              <h5 style={styles.breakdownTitle}>
                <i
                  className="fa-solid fa-list"
                  style={{ marginRight: "8px" }}
                ></i>
                Desglose por Método de Transporte
              </h5>
              <div style={styles.breakdownGrid}>
                {transportMethods.map((method, index) => (
                  <div key={index} style={styles.breakdownCard}>
                    <div style={styles.breakdownMethodName}>{method.name}</div>
                    <div style={styles.breakdownCount}>{method.count}</div>
                    <div style={styles.breakdownPercentage}>
                      {method.percentage}%
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Gráfica */}
          <div
            style={{
              ...styles.chartBody,
              flex: 1,
              minHeight: 0,
              padding: "16px",
            }}
          >
            {chartData.length === 0 ? (
              <div style={styles.noDataContainer}>
                <i
                  className="fa-solid fa-chart-line"
                  style={styles.noDataIcon}
                ></i>
                <p style={styles.noDataText}>
                  No hay datos disponibles para mostrar
                </p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={chartData}
                  margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                >
                  <defs>
                    {allMethods.map((method) => {
                      const color = getColorForMethod(method);
                      const gradientId = getGradientId(method);
                      return (
                        <linearGradient
                          key={gradientId}
                          id={gradientId}
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor={color}
                            stopOpacity={0.3}
                          />
                          <stop
                            offset="95%"
                            stopColor={color}
                            stopOpacity={0}
                          />
                        </linearGradient>
                      );
                    })}
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#e5e7eb"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    tick={{ fontSize: 11, fill: "#6b7280" }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: "#6b7280" }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <Legend iconType="circle" />
                  {allMethods.map((method) => {
                    const color = getColorForMethod(method);
                    const gradientId = getGradientId(method);
                    return (
                      <Area
                        key={method}
                        type="monotone"
                        dataKey={method}
                        stroke={color}
                        strokeWidth={2}
                        fillOpacity={1}
                        fill={`url(#${gradientId})`}
                        name={method}
                        dot={{ fill: color, r: 3, strokeWidth: 0 }}
                        activeDot={{ r: 5, strokeWidth: 0 }}
                      />
                    );
                  })}
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Columna derecha: Últimos cambios de transporte */}
        <div style={styles.changesColumn}>
          <div style={styles.changesHeader}>
            <h5 style={styles.changesTitle}>
              <i
                className="fa-solid fa-exchange-alt"
                style={{ marginRight: "8px", color: "#10b981" }}
              ></i>
              Últimos Cambios de Transporte ({transportChanges.length})
            </h5>
          </div>
          <div style={styles.changesListContainer}>
            {transportChanges.length > 0 ? (
              <div style={styles.listContainer}>
                {transportChanges.map((change) => {
                  const tooltipContent = (
                    <div style={{ padding: "4px" }}>
                      <div style={styles.tooltipRow}>
                        <i
                          className="fa-solid fa-user"
                          style={styles.tooltipIcon}
                        ></i>
                        <span style={styles.tooltipLabel}>Nombre:</span>
                        <span style={styles.tooltipValue}>
                          {toTitleCase(change.name)}
                        </span>
                      </div>
                      <div style={styles.tooltipRow}>
                        <i
                          className="fa-solid fa-calendar"
                          style={styles.tooltipIcon}
                        ></i>
                        <span style={styles.tooltipLabel}>Fecha:</span>
                        <span style={styles.tooltipValue}>
                          {formatDateSpanish(change.changedAt)}
                        </span>
                      </div>
                      <div style={styles.tooltipRow}>
                        <i
                          className="fa-solid fa-building"
                          style={styles.tooltipIcon}
                        ></i>
                        <span style={styles.tooltipLabel}>Sucursal:</span>
                        <span style={styles.tooltipValue}>{change.branch}</span>
                      </div>
                      <div style={styles.tooltipRow}>
                        <i
                          className="fa-solid fa-arrow-left"
                          style={styles.tooltipIcon}
                        ></i>
                        <span style={styles.tooltipLabel}>
                          Método Anterior:
                        </span>
                        <span style={styles.tooltipValue}>
                          {change.previousMethod}
                        </span>
                      </div>
                      <div style={styles.tooltipRow}>
                        <i
                          className="fa-solid fa-arrow-right"
                          style={styles.tooltipIcon}
                        ></i>
                        <span style={styles.tooltipLabel}>Método Nuevo:</span>
                        <span style={styles.tooltipValue}>
                          {change.newMethod}
                        </span>
                      </div>
                    </div>
                  );

                  return (
                    <Tooltip
                      key={change.id}
                      title={tooltipContent}
                      placement="right"
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: "#1f2937",
                            color: "#fff",
                            fontSize: "12px",
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                            maxWidth: "none",
                          },
                        },
                        arrow: {
                          sx: {
                            color: "#1f2937",
                          },
                        },
                      }}
                    >
                      <div
                        style={styles.card}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          setContextMenu(
                            contextMenu === null
                              ? {
                                  mouseX: e.clientX + 2,
                                  mouseY: e.clientY - 6,
                                  partnerId: change.partnerId,
                                }
                              : null
                          );
                        }}
                      >
                        <div style={styles.avatarContainer}>
                          {change.profileImage ? (
                            <img
                              src={change.profileImage}
                              alt={toTitleCase(change.name)}
                              style={styles.avatarImage}
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                                const icon = e.currentTarget
                                  .nextElementSibling as HTMLElement;
                                if (icon) icon.style.display = "flex";
                              }}
                            />
                          ) : null}
                          <div
                            style={{
                              ...styles.avatarIcon,
                              display: change.profileImage ? "none" : "flex",
                            }}
                          >
                            <i
                              className="fa-regular fa-user"
                              style={styles.avatarIconInner}
                            ></i>
                          </div>
                        </div>
                        <div style={styles.cardContent}>
                          <div style={styles.cardName}>
                            {toTitleCase(change.name)}
                          </div>
                          <div style={styles.cardPosition}>
                            {change.previousMethod} → {change.newMethod}
                          </div>
                          <div style={styles.cardDate}>
                            {formatDateSpanish(change.changedAt)}
                          </div>
                        </div>
                      </div>
                    </Tooltip>
                  );
                })}
              </div>
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: "24px",
                  color: "#6b7280",
                }}
              >
                <i
                  className="fa-solid fa-check-circle"
                  style={{
                    fontSize: "24px",
                    color: "#22c55e",
                    marginBottom: "8px",
                  }}
                ></i>
                <p style={{ fontSize: "12px", fontWeight: 500, margin: 0 }}>
                  No hay cambios de transporte en el período seleccionado
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <Menu
        open={contextMenu !== null}
        onClose={() => setContextMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        PaperProps={{
          style: {
            backgroundColor: "#ffffff",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
            borderRadius: "8px",
          },
        }}
      >
        <MenuItem
          style={{
            display: "flex",
            alignItems: "center",
            padding: "12px 16px",
            fontSize: "16px",
          }}
          onClick={() => {
            if (contextMenu) {
              const url = `${window.location.origin}/colaboradores/detalles/${contextMenu.partnerId}`;
              window.open(url, "_blank", "noopener,noreferrer");
            }
            setContextMenu(null);
          }}
        >
          <i
            className="fa-regular fa-user"
            style={{ marginRight: "12px", fontSize: "18px" }}
          ></i>
          Perfil
        </MenuItem>
      </Menu>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  chartCard: {
    background: "#fff",
    borderRadius: "16px",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
    overflow: "hidden",
    flex: 1,
  },
  chartHeader: {
    background: "linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)",
    padding: "20px 28px",
    borderBottom: "1px solid #e5e7eb",
  },
  chartHeaderContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
  },
  chartTitle: {
    margin: 0,
    fontSize: "18px",
    fontWeight: "600",
    color: "#2d3748",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flex: 1,
  },
  chartTitleIcon: {
    fontSize: "20px",
    color: "#10b981",
  },
  chartBody: {
    padding: "28px",
  },
  mainContent: {
    display: "flex",
    gap: "20px",
    padding: "20px 28px",
    flex: 1,
    minHeight: 0,
  },
  statsColumn: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    minWidth: 0,
  },
  changesColumn: {
    width: "400px",
    display: "flex",
    flexDirection: "column",
    borderLeft: "1px solid #e5e7eb",
    paddingLeft: "20px",
  },
  customTooltip: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    padding: "12px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  },
  tooltipLabel: {
    fontWeight: "600",
    marginBottom: "8px",
    color: "#2d3748",
  },
  statsBadge: {
    background: "#10b981",
    borderRadius: "6px",
    padding: "6px 12px",
  },
  statsText: {
    color: "#fff",
    fontSize: "12px",
    fontWeight: "600",
  },
  statsSection: {
    display: "flex",
    gap: "16px",
    padding: "20px 28px",
    borderBottom: "1px solid #e5e7eb",
    background: "#f9fafb",
  },
  statCard: {
    flex: 1,
    background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
    borderRadius: "12px",
    padding: "16px 20px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  },
  statIconWrapper: {
    width: "48px",
    height: "48px",
    borderRadius: "50%",
    background: "rgba(255, 255, 255, 0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  statIcon: {
    fontSize: "20px",
    color: "#fff",
  },
  statContent: {
    flex: 1,
  },
  statValue: {
    fontSize: "28px",
    fontWeight: "700",
    color: "#fff",
    lineHeight: 1,
  },
  statLabel: {
    fontSize: "13px",
    color: "rgba(255, 255, 255, 0.9)",
    marginTop: "4px",
  },
  breakdownSection: {
    padding: "20px 28px",
    borderBottom: "1px solid #e5e7eb",
    background: "#fff",
  },
  breakdownTitle: {
    margin: "0 0 16px 0",
    fontSize: "16px",
    fontWeight: "600",
    color: "#2d3748",
    display: "flex",
    alignItems: "center",
  },
  breakdownGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: "12px",
  },
  breakdownCard: {
    background: "#f9fafb",
    borderRadius: "8px",
    padding: "12px",
    border: "1px solid #e5e7eb",
    textAlign: "center",
  },
  breakdownMethodName: {
    fontSize: "13px",
    color: "#6b7280",
    marginBottom: "8px",
    fontWeight: "500",
  },
  breakdownCount: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#2d3748",
    marginBottom: "4px",
  },
  breakdownPercentage: {
    fontSize: "12px",
    color: "#10b981",
    fontWeight: "600",
  },
  noDataContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    minHeight: "300px",
    color: "#9ca3af",
  },
  noDataIcon: {
    fontSize: "48px",
    marginBottom: "16px",
    opacity: 0.5,
  },
  noDataText: {
    fontSize: "16px",
    margin: 0,
  },
  changesHeader: {
    marginBottom: "16px",
    paddingBottom: "12px",
    borderBottom: "1px solid #e5e7eb",
  },
  changesTitle: {
    margin: 0,
    fontSize: "16px",
    fontWeight: "600",
    color: "#2d3748",
    display: "flex",
    alignItems: "center",
  },
  changesListContainer: {
    maxHeight: "320px",
    overflowY: "auto",
    overflowX: "hidden",
    padding: "12px 0",
    minHeight: 0,
  },
  listContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  card: {
    background: "#fff",
    borderRadius: "6px",
    padding: "6px 10px",
    transition: "all 0.2s ease",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    border: "1px solid #f3f4f6",
  },
  avatarContainer: {
    width: "28px",
    height: "28px",
    borderRadius: "50%",
    backgroundColor: "#f3f4f6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    overflow: "hidden",
  },
  avatarImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  avatarIcon: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f3f4f6",
  },
  avatarIconInner: {
    color: "#9ca3af",
    fontSize: "12px",
  },
  cardContent: {
    flex: 1,
    minWidth: 0,
  },
  cardName: {
    fontSize: "12px",
    fontWeight: "500",
    color: "#374151",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  cardPosition: {
    fontSize: "10px",
    color: "#6b7280",
    marginTop: "2px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  cardDate: {
    fontSize: "9px",
    color: "#9ca3af",
    marginTop: "2px",
    fontStyle: "italic",
  },
  tooltipRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    marginBottom: "6px",
  },
  tooltipIcon: {
    fontSize: "11px",
    color: "#9ca3af",
    width: "14px",
    flexShrink: 0,
    marginTop: "2px",
  },
  tooltipLabel: {
    color: "#d1d5db",
    fontWeight: 500,
    marginRight: "6px",
    flexShrink: 0,
  },
  tooltipValue: {
    color: "#fff",
    fontWeight: 400,
    wordBreak: "break-word",
  },
};

export default TransportStats;
